// SAML single sign-on, the browser half of SamlController (app/controllers/saml_controller.rb).
//
// Every SSO sign-in starts at /auth/saml/<connection>/login on this origin. The server sends
// people there in three ways, and the SPA follows each:
// - "Log in with SSO" on the login page asks /auth/saml/discover for the typed email's
//   connection (discoverSsoSignIn).
// - A password sign-in, password reset, or organization switch into an organization that requires
//   SSO is refused with `sso_url` in the error body (requiredSsoUrl).
// - A session the server ends because its organization now requires SSO answers 401 with
//   `sso_url` (redirectToLogin).
// The way back is the same as Microsoft's: SSO_RETURN_PATH_KEY, which LoginMicrosoft reads on
// /app/login_microsoft, where every SSO sign-in lands.
import axios from 'axios';
import { parse, stringify } from 'qs';
import { SSO_RETURN_PATH_KEY, loginPrinterSignInRedirect, printerSignInRedirect } from './appSignIn';
import sameOriginAppPath from './sameOriginAppPath';

// Only a same-origin SSO start path is followed; anything else in an error body is ignored.
const SSO_LOGIN_PATH = /^\/auth\/saml\/[A-Za-z0-9-]+\/login(?:\?[^#]*)?$/;

export const DISCOVER_PATH = '/auth/saml/discover';
export const NOT_SET_UP_MESSAGE = 'Single sign-on isn\'t set up for this email address.';

// What the login page says for each `?ssoError=` the server redirects back with.
export const SSO_ERROR_MESSAGES = {
  unavailable: 'Single sign-on isn\'t available for your organization right now. Sign in with your password or contact your administrator.',
  expired: 'Your single sign-on attempt expired. Please try again.',
  replayed: 'Your single sign-on attempt expired. Please try again.',
  invalid_response: 'We couldn\'t verify the response from your organization\'s sign-in. Please try again or contact your administrator.',
  account_not_found: 'Your organization\'s sign-in worked, but there\'s no Medsender account for you in this organization. Contact your Medsender administrator.',
  domain_not_allowed: 'Your email domain isn\'t set up for single sign-on in this organization. Contact your Medsender administrator.',
  no_email: 'Your organization\'s sign-in didn\'t share your email address with Medsender. Contact your administrator.',
  sso_required: 'Your organization requires single sign-on. Use Log in with SSO.',
  // "Ask for Microsoft sign-in every time": the response did not come from a fresh sign-in.
  reauthentication_required: 'Your organization requires you to sign in to Microsoft again. Please try again.',
};
const DEFAULT_SSO_ERROR_MESSAGE = 'Single sign-on failed. Please try again.';

/**
 * The login page's message for a `?ssoError=` code, or null when there is none.
 *
 * @param {*} code
 * @returns {string|null}
 */
export const ssoErrorMessage = (code) => {
  if (!code) return null;
  return SSO_ERROR_MESSAGES[code] || DEFAULT_SSO_ERROR_MESSAGE;
};

/**
 * The value when it is an SSO start path on this origin, otherwise null.
 *
 * @param {*} value
 * @returns {string|null}
 */
export const ssoSignInUrl = (value) => (
  typeof value === 'string' && SSO_LOGIN_PATH.test(value) ? value : null
);

/**
 * The SSO start path a refused request names (`sso_url` in its error body), or null.
 *
 * @param {*} error an axios error, or crud's `{ response, error: true }`
 * @returns {string|null}
 */
export const requiredSsoUrl = (error) => ssoSignInUrl(error?.response?.data?.sso_url);

/**
 * The SSO start path with the printer sign-in page as its `origin`, so the sign-in comes back
 * through it (SamlController honours only printer sign-in origins).
 *
 * @param {string} url an SSO start path
 * @param {string|null} origin the printer sign-in page, or null
 * @returns {string}
 */
export const withPrinterOrigin = (url, origin) => {
  if (!origin) return url;
  const [path, query = ''] = url.split('?');
  const params = { ...parse(query), origin };
  return `${path}?${stringify(params)}`;
};

/**
 * Where a sign-in from the login page should end up, as saveCurrentUrl keeps it for Microsoft:
 * the login's ?redirect=, or the string 'null' (LoginMicrosoft then goes to /app).
 *
 * @returns {string}
 */
export const loginReturnPath = () => {
  const { redirect } = parse(window.location.search, { ignoreQueryPrefix: true });
  return typeof redirect === 'string' && redirect !== '' ? redirect : 'null';
};

/**
 * Where /app/login_microsoft (every Microsoft and SAML sign-in lands there) goes next: the path
 * the sign-in's start kept, when it is an app path on this origin, otherwise /app. A sign-in that
 * did not start from this browser's login (the Entra My Apps tile, the connection's Sign on URL, a
 * bookmark) kept nothing, and the login keeps the string 'null' when it had no redirect.
 *
 * @param {*} stored the SSO_RETURN_PATH_KEY value
 * @returns {string}
 */
export const ssoReturnDestination = (stored) => sameOriginAppPath(stored) || '/app';

/**
 * Where /app/login_microsoft goes next: the printer sign-in page its own ?redirect= names, else
 * ssoReturnDestination(stored). A page load of the printer sign-in page refused by Require SSO
 * goes through SSO from the server, before the SPA runs, so nothing was stored and only the
 * ?redirect= SamlController#complete adds leads back.
 *
 * @param {string} search /app/login_microsoft's query string
 * @param {*} stored the SSO_RETURN_PATH_KEY value
 * @returns {string}
 */
export const loginMicrosoftDestination = (search, stored) => (
  printerSignInRedirect(search) ?? ssoReturnDestination(stored)
);

/**
 * Sends the browser to an SSO sign-in, keeping where it should return to.
 *
 * @param {string} url an SSO start path; anything else is refused
 * @param {Object} [options]
 * @param {string} [options.returnPath] stored under SSO_RETURN_PATH_KEY ('null' for /app)
 * @returns {boolean} whether the browser is leaving
 */
export const startSsoSignIn = (url, { returnPath = 'null' } = {}) => {
  const target = ssoSignInUrl(url);
  if (!target) return false;

  try {
    sessionStorage.setItem(SSO_RETURN_PATH_KEY, returnPath);
  } catch {
    // Without storage the sign-in still works; it returns to /app.
  }
  window.location.assign(target);
  return true;
};

/**
 * Follows a refused request's `sso_url`, from the login page or a page reached from it: the
 * login's ?redirect= is the way back and its printer sign-in page rides along as `origin`.
 *
 * @param {*} error
 * @returns {boolean} whether the browser is leaving
 */
export const startRequiredSsoSignIn = (error) => {
  const url = requiredSsoUrl(error);
  if (!url) return false;

  const origin = loginPrinterSignInRedirect(window.location.search);
  return startSsoSignIn(withPrinterOrigin(url, origin), { returnPath: loginReturnPath() });
};

/**
 * Asks the server which SSO sign-in an email uses.
 *
 * @param {string} email
 * @param {string|null} origin the printer sign-in page the login is on its way to, or null
 * @returns {Promise<{ redirectUrl: string|null, error: string|null }>}
 */
export const discoverSsoSignIn = async (email, origin) => {
  try {
    const response = await axios({
      method: 'POST',
      url: DISCOVER_PATH,
      data: origin ? { email, origin } : { email },
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0]?.content,
      },
    });
    const redirectUrl = ssoSignInUrl(response?.data?.redirect_url);
    return redirectUrl ? { redirectUrl, error: null } : { redirectUrl: null, error: NOT_SET_UP_MESSAGE };
  } catch (error) {
    const data = error?.response?.data || {};
    return { redirectUrl: null, error: data.error || data.message || NOT_SET_UP_MESSAGE };
  }
};
